import { useEffect, useState } from 'react';
import {
  TenantServicePackage,
  getTenantServicePackage,
  applyTenantServicePackage,
  extractApiError,
} from '../services/api';

/**
 * Service-Pakete (Achse B) auf der Tenant-Detailseite.
 *
 * Zeigt die immer enthaltene Core-Suite und erlaubt dem Operator,
 * Module und Bundles per Checkbox zu aktivieren/deaktivieren. "Anwenden"
 * ruft PUT /api/tenants/:slug/service-package -> install/uninstall der
 * betroffenen Marketplace-Apps + Audit.
 */
export function ServicePackagePanel({ slug, isReadOnly }: { slug: string; isReadOnly: boolean }) {
  const [data, setData] = useState<TenantServicePackage | null>(null);
  const [modules, setModules] = useState<Set<string>>(new Set());
  const [bundles, setBundles] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function seed(d: TenantServicePackage) {
    setData(d);
    setModules(new Set(d.activeModules));
    // Ein Bundle gilt als ausgewaehlt, wenn alle seine Module aktiv sind.
    const active = new Set(d.activeModules);
    setBundles(new Set(d.catalog.bundles.filter((b) => b.modules.every((m) => active.has(m))).map((b) => b.id)));
  }

  useEffect(() => {
    getTenantServicePackage(slug).then(seed).catch((err) => setError(extractApiError(err)));
  }, [slug]);

  function toggle(set: Set<string>, setter: (s: Set<string>) => void, id: string) {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  }

  async function apply() {
    setBusy(true); setError(null); setNotice(null);
    try {
      const res = await applyTenantServicePackage(slug, {
        modules: Array.from(modules),
        bundles: Array.from(bundles),
      });
      setNotice(
        `Angewendet: ${res.added.length} hinzugefügt, ${res.removed.length} entfernt.`,
      );
      const fresh = await getTenantServicePackage(slug);
      seed(fresh);
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setBusy(false);
    }
  }

  if (!data) {
    return (
      <div className="bg-white rounded shadow p-4 mb-6">
        <h3 className="font-semibold mb-2">Service-Paket</h3>
        {error ? <p className="text-sm text-red-600">{error}</p> : <p className="text-sm text-slate-400">Lade…</p>}
      </div>
    );
  }

  return (
    <div className="bg-white rounded shadow p-4 mb-6">
      <h3 className="font-semibold mb-2">Service-Paket</h3>

      {error && <div className="bg-red-100 text-red-700 p-2 rounded mb-3 text-sm">{error}</div>}
      {notice && <div className="bg-green-50 border border-green-200 text-green-800 p-2 rounded mb-3 text-sm">{notice}</div>}

      <div className="mb-3">
        <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Core-Suite (immer enthalten)</p>
        <div className="flex flex-wrap gap-2">
          {data.catalog.core.map((s) => (
            <span key={s} className="badge bg-slate-200 font-mono">{s}</span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Bundles</p>
          {data.catalog.bundles.map((b) => (
            <label key={b.id} className="flex items-start gap-2 mb-1 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={bundles.has(b.id)}
                disabled={isReadOnly || busy}
                onChange={() => toggle(bundles, setBundles, b.id)}
              />
              <span><strong>{b.name}</strong><span className="block text-slate-500 text-xs">{b.description}</span></span>
            </label>
          ))}
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Module</p>
          {data.catalog.modules.map((m) => (
            <label key={m.id} className="flex items-start gap-2 mb-1 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={modules.has(m.id)}
                disabled={isReadOnly || busy}
                onChange={() => toggle(modules, setModules, m.id)}
              />
              <span><strong>{m.name}</strong><span className="block text-slate-500 text-xs">{m.description}</span></span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 mt-3">
        <button onClick={apply} disabled={isReadOnly || busy} className="btn-primary disabled:opacity-50">
          {busy ? 'Wende an…' : 'Paket anwenden'}
        </button>
        <span className="text-xs text-slate-500">
          Aktive Dienste: {data.services.length > 0 ? data.services.join(', ') : '—'}
        </span>
      </div>
      {isReadOnly && <p className="text-xs text-slate-400 mt-2">Read-only-Operator; Änderungen deaktiviert.</p>}
    </div>
  );
}
